<script>
  import { ComponentType } from '../../../../../../js/constants';
  import { getNavState } from '../../../../../../js/stores';
  import { blurOnClick } from '../../../../../../js/blurOnClick';
  import { getCategory, getSubcategory, getNavDeviceType } from '../../../../../../js/trackingUtils';
  import { trackCdp } from '../../../../../../js/tracking';

  import CategoryList from './CategoryList/CategoryList.svelte';

  export let id;
  export let title;
  export let navItems;
  export let spotlightConfig = undefined;

  const navState = getNavState();
  const trackData = { menuId: id, menuText: title };

  let element;
  let isOpen = false;

  // When $navState updates, if the current category
  // is not this element, close this category menu
  // to ensure only one category is open at a time.
  $: if (isOpen && $navState.category !== element) {
    close();
  }

  function open() {
    isOpen = true;

    // when a category opens, close any open subcategory
    $navState = { ...$navState, category: element, subcategory: null };

    trackCdp({
      eventName: 'Open Menu',
      properties: {
        ...trackData,
        menuCategory: getCategory(element),
        menuSubcategory: getSubcategory(element),
        menuLocation: ComponentType.MAIN_NAVIGATION,
        navDeviceType: getNavDeviceType(element),
      },
    });
  }

  function close() {
    isOpen = false;

    // when a category closes, close any open subcategory
    $navState = { ...$navState, subcategory: null };

    trackCdp({
      eventName: 'Close Menu',
      properties: {
        ...trackData,
        menuCategory: getCategory(element),
        menuSubcategory: getSubcategory(element),
        navDeviceType: getNavDeviceType(element),
      },
    });
  }

  function onToggle() {
    isOpen ? close() : open();
  }
</script>

<div
  {id}
  class="Category"
  class:isOpen
  data-category={title}
  data-component-type={ComponentType.MAIN_NAVIGATION_MENU}
  bind:this={element}
>
  <button
    class="toggle nav-button"
    class:isOpen
    aria-haspopup="true"
    aria-expanded={isOpen ? true : false}
    use:blurOnClick
    on:click={onToggle}
  >
    <span class="title">{title}</span>
  </button>
  <CategoryList {isOpen} {navItems} {spotlightConfig} />
</div>

<style>@charset "UTF-8";
.Category {
  position: relative;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
  margin: 0;
  padding: 0;
  background-color: var(--color-background, #fff);
}
.Category::-moz-focus-inner {
  border: 0;
}
.Category:focus {
  outline: none;
}
.Category::before {
  content: "";
  pointer-events: none;
  display: block;
  position: absolute;
  inset: 0;
  background-color: var(--color-highlight, rgba(26, 26, 26, 0.1));
  border-radius: 0;
  opacity: 0;
  transition: opacity 300ms cubic-bezier(0.4, 0, 0.2, 1), transform 300ms cubic-bezier(0.4, 0, 0.2, 1);
}
.Category:focus:not(:active)::before {
  opacity: 1;
  transform: scale(1);
}
.Category.isOpen::before {
  opacity: 1;
  transform: scale(1);
}
.Category > * {
  position: relative;
}

.toggle.nav-button {
  cursor: pointer;
  appearance: none;
  box-sizing: border-box;
  overflow: hidden;
  display: block;
  position: relative;
  width: 100%;
  margin: 0;
  padding: 0;
  outline: 0;
  border: 0;
  color: var(--color-text, #1a1a1a);
  font-family: "Square Sans Text VF", "Square Sans Text", "Helvetica", "Arial", "Hiragino Kaku Gothic Pro", "ヒラギノ角ゴ Pro W3", "メイリオ", meiryo, "ＭＳ Ｐゴシック", sans-serif;
  font-size: 16px;
  line-height: 28px;
  font-weight: 400;
  letter-spacing: normal;
  text-align: left;
  text-decoration: none;
  user-select: none;
  -webkit-user-drag: none;
  -webkit-tap-highlight-color: transparent;
  position: relative;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
  background-color: transparent;
  padding: 20px 50px 20px 25px;
  transition: none;
}
.toggle.nav-button::after {
  content: "";
  display: block;
  position: absolute;
  width: 8px;
  height: 8px;
  right: 35px;
  border-style: solid;
  border-width: 0;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-color: var(--color-text, #1a1a1a);
  transform: translate3d(50%, calc(-50% - 2px), 0) rotate(45deg);
  transition: transform 300ms cubic-bezier(0.4, 0, 0.2, 1);
  top: 34px;
}
.toggle.nav-button.isOpen::after {
  transform: translate3d(50%, calc(-50% + 2px), 0) rotate(-135deg);
}
.toggle.nav-button::-moz-focus-inner {
  border: 0;
}
.toggle.nav-button:focus {
  outline: none;
}
.toggle.nav-button::before {
  content: "";
  pointer-events: none;
  display: block;
  position: absolute;
  inset: 0;
  background-color: var(--color-highlight, rgba(26, 26, 26, 0.1));
  border-radius: 0;
  opacity: 0;
  transition: opacity 100ms cubic-bezier(0.4, 0, 0.2, 1), transform 100ms cubic-bezier(0.4, 0, 0.2, 1);
  transform: scale(0);
}
.toggle.nav-button:focus:not(:active)::before {
  opacity: 1;
  transform: scale(1);
}
.toggle.nav-button > * {
  position: relative;
}

.title {
  position: relative;
  font-weight: var(--font-weight-medium, 500);
}</style>
